<template>
  <div class="qvp-kids-exam-center-list">
    <!-- Zone admins: show nothing, redirect to form in beforeMount -->
    <div v-if="!isStateOrDistrictAdmin" class="text-center py-4">
      <div class="spinner-border text-primary" role="status"></div>
    </div>

    <template v-else>
      <div class="page-header">
        <h3 class="page-title mal-text">QVP Kids പരീക്ഷാ കേന്ദ്രങ്ങൾ (Exam Centers List)</h3>
        <p class="page-subtitle mal-text">സ്റ്റേറ്റ് / ജില്ലാ അഡ്മിൻ കാണാവുന്ന പരീക്ഷാ കേന്ദ്രങ്ങളുടെ പട്ടിക</p>
      </div>

      <div class="readonly-banner mal-text">
        സോണൽ അഡ്മിൻ ചേർത്ത QVP Kids പരീക്ഷാ കേന്ദ്രങ്ങളുടെ പട്ടിക ഇവിടെ നിന്നും കാണാം.
      </div>

      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status"></div>
      </div>

      <div v-else-if="savedCenters.length > 0" class="saved-centers-section">
        <h4 class="section-title mal-text">പരീക്ഷാ കേന്ദ്രങ്ങൾ</h4>
        <div class="saved-centers-list">
          <div
            v-for="(center, idx) in savedCenters"
            :key="center.id || idx"
            class="saved-center-card"
          >
            <div class="center-info">
              <strong class="center-name">{{ getCenterName(center) }}</strong>
              <template v-if="userLevel == 1">
                <span class="center-detail" v-if="center.district_name">ജില്ല: {{ center.district_name }}</span>
                <span class="center-detail" v-if="center.zone_name">സോൺ: {{ center.zone_name }}</span>
              </template>
              <template v-else-if="userLevel == 2">
                <span class="center-detail" v-if="center.zone_name">സോൺ: {{ center.zone_name }}</span>
              </template>
              <span class="center-detail">കൺവീനർ: {{ center.convenor_name }}</span>
              <span class="convenor-phone">
                <a v-if="center.convenor_phone" :href="'tel:' + center.convenor_phone">{{ center.convenor_phone }}</a>
                <template v-else>{{ center.convenor_phone || '-' }}</template>
              </span>
              <span class="center-detail" v-if="center.exam_date || center.exam_time">
                {{ formatDate(center.exam_date) }}{{ center.exam_date && center.exam_time ? ' | ' : '' }}{{ center.exam_time || '' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="empty-note mal-text">
        ഇതുവരെ പരീക്ഷാ കേന്ദ്രങ്ങൾ ചേർത്തിട്ടില്ല.
      </div>
    </template>
  </div>
</template>

<script>
import UserService from '../services/user.service';

export default {
  name: 'QVPKidsExamCenterList',
  data() {
    return {
      loading: false,
      savedCenters: [],
      madrasas: [],
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    subLevel() {
      const raw = this.$route.params.sub_level;
      const n = parseInt(String(raw), 10);
      return Number.isNaN(n) ? 0 : n;
    },
    zoneId() {
      if (this.subLevel !== 3) return null;
      const id = this.$route.params.id;
      if (id) return parseInt(id, 10);
      return null;
    },
    districtId() {
      if (this.subLevel !== 2) return null;
      const id = this.$route.params.id;
      if (id) return parseInt(id, 10);
      return null;
    },
    stateId() {
      if (this.subLevel !== 1) return null;
      const id = this.$route.params.id;
      if (id) return parseInt(id, 10);
      return null;
    },
    userLevel() {
      const raw = this.currentUser?.user_level;
      if (raw == null) return 0;
      const n = parseInt(String(raw), 10);
      return Number.isNaN(n) ? 0 : n;
    },
    isZoneAdmin() {
      const raw = this.currentUser?.user_level;
      return raw == 3 || this.userLevel === 3;
    },
    isStateOrDistrictAdmin() {
      return this.userLevel == 1 || this.userLevel == 2;
    },
  },
  beforeMount() {
    if (this.subLevel == 3) {
      this.$router.replace(`/org/3/${this.zoneId || this.currentUser?.zone_id || this.$route.params.id}/qvp-kids-exam-center`);
    }
  },
  mounted() {
    this.$root.updateParent('QVP Kids പരീക്ഷാ കേന്ദ്രങ്ങൾ');
    if (this.isStateOrDistrictAdmin) {
      this.fetchSavedCenters();
    }
  },
  watch: {
    '$route.params.id'(newVal, oldVal) {
      if (newVal !== oldVal && this.isStateOrDistrictAdmin) {
        this.fetchSavedCenters();
      }
    },
  },
  methods: {
    async fetchSavedCenters() {
      this.loading = true;
      this.savedCenters = [];
      try {
        if (this.subLevel == 3 && this.zoneId) {
          const response = await UserService.authGetRequest('qvp-kids-exam-centers', {
            zone_id: this.zoneId,
          });
          if (response.data.status === 'success' && Array.isArray(response.data.data?.list)) {
            this.savedCenters = response.data.data.list;
          }
          return;
        }

        if (this.subLevel == 2 && this.districtId) {
          const response = await UserService.authGetRequest('qvp-kids-exam-centers', {
            district_id: this.districtId,
          });
          if (response.data.status === 'success' && Array.isArray(response.data.data?.list)) {
            this.savedCenters = response.data.data.list;
          }
          return;
        }

        if (this.subLevel == 1) {
          const response = await UserService.authGetRequest('qvp-kids-exam-centers', {
            user_level: 1,
          });
          if (response.data.status === 'success' && Array.isArray(response.data.data?.list)) {
            this.savedCenters = response.data.data.list;
          }
        }
      } catch (err) {
        console.warn('Could not load saved centers:', err);
        this.savedCenters = [];
      } finally {
        this.loading = false;
      }
    },
    formatDate(d) {
      if (!d) return '';
      const dt = new Date(d + 'T00:00:00');
      if (isNaN(dt.getTime())) return d;
      const day = String(dt.getDate()).padStart(2, '0');
      const month = String(dt.getMonth() + 1).padStart(2, '0');
      const year = dt.getFullYear();
      return `${day}-${month}-${year}`;
    },
    getCenterName(center) {
      if (center.madrasa_name) return center.madrasa_name;
      if (center.madrasa && center.madrasa.name) return center.madrasa.name;
      const m = this.madrasas.find((x) => x.id === center.madrasa_id || x.id == center.madrasa_id);
      return (m && m.name) ? m.name : (center.convenor_name || '');
    },
  },
};
</script>

<style scoped>
.qvp-kids-exam-center-list {
  max-width: 480px;
  margin: 0 auto;
  padding: 16px;
}
.page-header {
  margin-bottom: 20px;
}
.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a418c;
  margin-bottom: 4px;
}
.page-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
}
.readonly-banner {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 20px;
  line-height: 1.5;
}
.saved-centers-section {
  margin-top: 8px;
}
.section-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1a418c;
}
.saved-centers-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.saved-center-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.center-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.center-info .center-name {
  font-size: 1rem;
  color: #1a418c;
}
.center-info .center-detail {
  font-size: 0.9rem;
  color: #6c757d;
}
.center-info .convenor-phone {
  font-size: 0.9rem;
  color: #1a418c;
}
.empty-note {
  margin-top: 18px;
  text-align: center;
  color: #6c757d;
}
</style>
